import { VocalLib, type PitchFrame } from '@tunasong/music-lib'

/**
 * One capture window: audition, count-in, mute, listen, hand back the frames.
 *
 * Every side effect is injected, so the ordering rules — the two that a broken tool would break
 * silently — are unit-testable without an audio graph:
 *
 *  1. **Playback is muted before a single frame is collected** (§6). With speakers, an unmuted
 *     reference bleeds into the capture and is scored as though the user had sung it. A silent
 *     user would then be handed a perfect score, which for a tool whose entire output is a
 *     measurement is the most dangerous thing it could do.
 *  2. **The window opens with a lead-in the user is not yet cued for** (§5.1). The voicing gate
 *     needs the first 500 ms as room tone to measure the noise floor from. Cue the singer inside
 *     that window and the floor rises to the level of their voice, after which nothing they sing
 *     clears it.
 *
 * The count-in and the reference both sound *before* the mute: they are the cue, and muting them
 * would leave the singer with nothing to sing against. They sound in that order — see
 * `runCaptureWindow`.
 *
 * Spec: docs/superpowers/specs/2026-09-06-vocal-pitch-training-design.md §5.1, §6
 */

/** `got-it` only when a take ends before its cap (Phase 2, Joni §5), on the way to `scoring` */
export type CapturePhase = 'count-in' | 'reference' | 'lead-in' | 'listening' | 'got-it' | 'scoring'

export interface CaptureWindowDeps {
  /** Mute every path that reaches the speakers. Called `true` before capture, `false` after. */
  setMuted: (muted: boolean) => void
  /** Subscribe to pitch frames. Resolves to an unsubscribe. */
  startFrames: (onFrame: (frame: PitchFrame) => void) => Promise<() => void>
  /** Audition the reference. Resolves when it has finished sounding. */
  playReference: () => Promise<void>
  /** One bar of clicks. Resolves when it has finished sounding. */
  playCountIn: () => Promise<void>
  /** Injected so tests need no fake timers, and so the sequence is assertable. */
  wait: (ms: number) => Promise<void>
  onPhase?: (phase: CapturePhase) => void
  /**
   * Every frame as it arrives, re-based, for the live input meter. The meter reads the same
   * frames the capture collects: a second stream for a meter would light a second microphone
   * indicator and could open a different device than the one being scored.
   */
  onFrame?: (frame: PitchFrame) => void
}

export interface CaptureWindowOptions {
  /** Room tone at the head of the window, for the noise floor. At least 500 ms (§5.1). */
  leadInMs: number
  /** Phrase duration plus a one-second tail (§6). With `earlyStop`, the cap. */
  captureMs: number
  /**
   * Pitch Trainer Phase 2 (Joni §5): end at the first of this much silence after the first sung frame, the
   * cap, or `done`. Silence before singing never ends a take (Bowie's pitfall 9). Phase 1 passes none.
   */
  earlyStop?: { silenceMs: number; done?: AbortSignal }
}

/** Whether a frame clears the voicing gate (§5.1), against the floor measured in the lead-in */
const isSung = (frame: PitchFrame, floor: number) =>
  frame.frequency > 0 &&
  frame.clarity >= VocalLib.voicingClarityMin &&
  20 * Math.log10(frame.rms / floor) >= VocalLib.voicingFloorDb

export const runCaptureWindow = async (
  deps: CaptureWindowDeps,
  options: CaptureWindowOptions
): Promise<PitchFrame[]> => {
  const { setMuted, startFrames, playReference, playCountIn, wait, onPhase, onFrame } = deps
  const { leadInMs, captureMs, earlyStop } = options

  /**
   * Count-in first, then the reference — a teacher's "here it comes, [plays], sing", not an
   * ensemble's "1, 2, 3, 4, go". Anything that sounds *after* the reference sits between the
   * pitch the singer heard and the pitch they are about to produce, and interpolated tones erode
   * pitch memory. Swapping these two lines is the whole of the bug this order prevents.
   */
  onPhase?.('count-in')
  await playCountIn()
  onPhase?.('reference')
  await playReference()

  const frames: PitchFrame[] = []
  /**
   * The worklet timestamps frames from its own construction, not from this window. On the second
   * item of a session the first frame arrives tens of seconds in, and `noiseFloorRms` — which
   * selects `time < 0.5` — would find no lead-in at all.
   */
  let origin: number | null = null
  /** Set once listening starts: the lead-in's noise floor, and the time of the last sung frame */
  let floor: number | null = null
  let lastSung: number | null = null
  let endEarly: () => void = () => undefined
  const earlyEnd = new Promise<'early'>(resolve => (endEarly = () => resolve('early')))

  setMuted(true)
  let ending: 'early' | 'cap' = 'cap'
  try {
    const stopFrames = await startFrames(frame => {
      origin = origin ?? frame.time
      const rebased = { ...frame, time: frame.time - origin }
      frames.push(rebased)
      onFrame?.(rebased)
      if (!earlyStop || floor === null) {
        return
      }
      if (isSung(rebased, floor)) {
        lastSung = rebased.time
      } else if (lastSung !== null && rebased.time - lastSung >= earlyStop.silenceMs / 1000) {
        endEarly()
      }
    })
    onPhase?.('lead-in')
    await wait(leadInMs)
    onPhase?.('listening')
    if (earlyStop) {
      floor = VocalLib.noiseFloorRms(frames)
      const { done } = earlyStop
      /** Done pressed during the lead-in still ends the take as soon as it can */
      if (done?.aborted) {
        endEarly()
      }
      done?.addEventListener('abort', endEarly, { once: true })
      ending = await Promise.race([wait(captureMs).then(() => 'cap' as const), earlyEnd])
      done?.removeEventListener('abort', endEarly)
    } else {
      await wait(captureMs)
    }
    stopFrames()
  } finally {
    setMuted(false)
  }

  if (ending === 'early') {
    onPhase?.('got-it')
  }
  onPhase?.('scoring')
  return frames
}
